import { useEffect, useMemo, useRef, useState } from 'react';
import { getFluidSectionHorizontalPaddingIndex, getFluidSectionHorizontalPaddingPx } from '@utils/fluidSectionHorizontalPadding';
import { Pagination, Scrollbar } from 'swiper/modules';
import debounce from 'lodash/debounce';
import { Swiper as SwiperClass } from 'swiper/types';
import { Swiper, SwiperSlide } from 'swiper/react';
import SlideControl from '@components/SlideControl/SlideControl';
import styles from '@components/InspirationSlider/InspirationSlider.styles';
import 'swiper/css';
import 'swiper/css/scrollbar';
import 'swiper/css/pagination';
import { useResponsive } from '@features/responsive';
type PropsType = {
  children: React.ReactNode;
  slidesPerView: number | 'auto';
  withScrollBar?: boolean;
  withPagination?: boolean;
  spaceBetween?: number;
  withOffset?: boolean;
  isArticles?: boolean;
  customOffset?: number[];
  fluidSectionOffset?: boolean;
  withoutPadding?: boolean;
  equalHeightSlides?: boolean;
};
const InspirationSlider = ({
  children,
  slidesPerView,
  withScrollBar = false,
  withPagination = false,
  spaceBetween = 4,
  withOffset = false,
  isArticles = false,
  customOffset,
  fluidSectionOffset = false,
  withoutPadding = false,
  equalHeightSlides = false
}: PropsType) => {
  const [shouldRenderArrows, SetShouldRenderArrows] = useState({
    left: false,
    right: false
  });
  const swiperInstance = useRef<SwiperClass>(null);
  const updateSwiper = debounce(() => {
    swiperInstance.current?.update();
  }, 500);
  const {
    isWiderThanDesktop,
    currentBreakpoint
  } = useResponsive();
  const paginationSettings = withPagination ? {
    clickable: true,
    bulletClass: 'bullet-pagination',
    currentClass: 'active'
  } : false;
  const scrollBarSettings = withScrollBar ? {
    draggable: true
  } : false;
  useEffect(() => {
    if (typeof window !== 'undefined') window.addEventListener('resize', updateSwiper);
    return () => window.removeEventListener('resize', updateSwiper);
  }, [updateSwiper]);
  const updateRenderArrows = () => {
    SetShouldRenderArrows({
      left: !swiperInstance.current?.isBeginning && !!swiperInstance.current?.allowSlidePrev,
      right: !swiperInstance.current?.isEnd && !!swiperInstance.current?.allowSlideNext
    });
  };
  const onSlideChange = () => {
    updateSwiper();
    updateRenderArrows();
  };
  const calculateOffset = () => {
    if (fluidSectionOffset) {
      const index = getFluidSectionHorizontalPaddingIndex(currentBreakpoint ?? 'mobile');
      return getFluidSectionHorizontalPaddingPx(index);
    }

    // Use custom offset if provided [mobile, mobilelg, tablet, tabletlg, desktop, widescreen]
    if (customOffset && customOffset.length > 0) {
      switch (currentBreakpoint) {
        case 'mobile':
          return customOffset[0] || 0;
        case 'mobilelg':
          return customOffset[1] || customOffset[0] || 0;
        case 'tablet':
          return customOffset[2] || customOffset[1] || customOffset[0] || 0;
        case 'tabletlg':
          return customOffset[3] || customOffset[2] || customOffset[1] || customOffset[0] || 0;
        case 'desktop':
          return customOffset[4] || customOffset[3] || customOffset[2] || 0;
        default:
          return customOffset[5] || customOffset[4] || customOffset[3] || 0;
      }
    }
    const mobileOffset = isArticles ? 40 : 24;
    if (withOffset) {
      switch (currentBreakpoint) {
        case 'mobile':
        case 'mobilelg':
          return isArticles ? 40 : 24;
        case 'tablet':
          return isArticles ? 64 : 24;
        default:
          return isWiderThanDesktop ? 195 * window.innerWidth / 1920 : mobileOffset;
      }
    }
    return 0;
  };
  const slidesOffset = calculateOffset();
  const onControlClick = (dir: 'next' | 'prev') => {
    if (dir === 'prev') swiperInstance.current?.slidePrev();
    if (dir === 'next') swiperInstance.current?.slideNext();
    updateRenderArrows();
  };
  useEffect(() => {
    updateRenderArrows();
  }, []);

  // WCAG 2.1.1 Keyboard - automatyczne przełączanie slajdu gdy focus trafia na element poza widocznym obszarem
  useEffect(() => {
    if (!swiperInstance.current) return;
    const swiper = swiperInstance.current;
    const handleFocusIn = (event: FocusEvent) => {
      const focusedElement = event.target as HTMLElement;
      if (!focusedElement) return;

      // Sprawdź czy element jest w kontenerze slidera
      const sliderContainer = swiper.el;
      if (!sliderContainer || !sliderContainer.contains(focusedElement)) return;

      // Znajdź slajd zawierający element z focusem
      const slideContainingFocus = focusedElement.closest('.swiper-slide') as HTMLElement;
      if (!slideContainingFocus) return;

      // Sprawdź czy slajd jest w widocznym obszarze
      const slideRect = slideContainingFocus.getBoundingClientRect();
      const containerRect = sliderContainer.getBoundingClientRect();
      const isVisible = slideRect.left >= containerRect.left && slideRect.right <= containerRect.right && slideRect.top >= containerRect.top && slideRect.bottom <= containerRect.bottom;
      if (isVisible) return;

      // Znajdź indeks slajdu i przełącz slider
      const slideIndex = Array.from(swiper.slides).indexOf(slideContainingFocus);
      if (slideIndex !== -1) {
        swiper.slideTo(slideIndex);
        updateRenderArrows();
      }
    };
    const sliderContainer = swiper.el;
    sliderContainer.addEventListener('focusin', handleFocusIn);
    return () => {
      sliderContainer.removeEventListener('focusin', handleFocusIn);
    };
  }, [children]);
  const renderSlides = useMemo(() => Array.isArray(children) ? children.map((child: JSX.Element) => <SwiperSlide key={Math.random()}>
            <div>{child}</div>
          </SwiperSlide>) : <SwiperSlide key={Math.random()}>
          <div>{children}</div>
        </SwiperSlide>, [children]);
  return <div sx={styles.sliderContainer(!!withPagination || !!withScrollBar, !!isArticles, !!withoutPadding, !!equalHeightSlides)}>
      <Swiper slidesPerView={slidesPerView} onSwiper={swiper => {
      swiperInstance.current = swiper;
    }} modules={[Scrollbar, Pagination]} onActiveIndexChange={onSlideChange} pagination={paginationSettings} speed={600} scrollbar={scrollBarSettings} spaceBetween={spaceBetween} slidesOffsetBefore={slidesOffset} slidesOffsetAfter={slidesOffset}>
        {renderSlides}
      </Swiper>
      {shouldRenderArrows.right && <SlideControl direction="next" onClick={onControlClick} withScrollbar={withScrollBar} />}
      {shouldRenderArrows.left && <SlideControl direction="prev" onClick={onControlClick} withScrollbar={withScrollBar} />}
    </div>;
};
export default InspirationSlider;